/**
 * @fileoverview Practitioner name-part splitter (MOR-771).
 *
 * The ATO Incap form asks for a representative's name split into Family name,
 * First given name, and Other given names. Practitioner names are stored as a
 * single full-name string (`practitioner_name` / `second_practitioner_name`),
 * so we derive the three parts on the fly at fill time. Nothing is stored and
 * there is no schema change — purely additive and backwards compatible.
 *
 * Split semantics differ from the contact First/Last splitter
 * (`contacts/nameParts.ts`): the ATO form treats the LAST whitespace token as
 * the family name, the first as the first given name, and any tokens between as
 * "other given names" (middle names). A single-token name is treated as a
 * family name (the ATO form's mandatory field). Hyphenated surnames written
 * with a hyphen ("Shaw-Gidley") stay one token and resolve correctly;
 * space-separated compound surnames are inherently ambiguous and are corrected
 * by editing the value in the field-review form before generation.
 *
 * Kept dependency-free so both the Convex resolver and any React consumer can
 * import it (mirrors `contactFields.ts` / `nameParts.ts`).
 */

export interface PractitionerNameParts {
  firstGivenName: string;
  otherGivenNames: string;
  familyName: string;
}

/** Collapse internal whitespace and trim. */
function clean(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Split a full name into ATO given/family parts. Last whitespace token is the
 * family name; the first is the first given name; anything between is the
 * other given names. A single-token name yields a family name only.
 */
export function splitPractitionerName(
  fullName: string | null | undefined,
): PractitionerNameParts {
  const tokens = clean(fullName).split(" ").filter(Boolean);
  if (tokens.length === 0) {
    return { firstGivenName: "", otherGivenNames: "", familyName: "" };
  }
  if (tokens.length === 1) {
    return { firstGivenName: "", otherGivenNames: "", familyName: tokens[0] };
  }
  return {
    firstGivenName: tokens[0],
    otherGivenNames: tokens.slice(1, -1).join(" "),
    familyName: tokens[tokens.length - 1],
  };
}
